import React, { useState } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {
  Box,
  IconButton,
  Tooltip as MuiTooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

const allLabels = [
  'Hasta 4 MDP',
  'Más de 4 MDP y hasta 100 MDP',
  'Más de 100 MDP y hasta 250 MDP',
  'Más de 250 MDP y hasta 1,000 MDP',
  'Más de 1,000 MDP y hasta 10,000 MDP',
  'Más de 10,000 MDP'
];

const dataIndustries2023 = {
  'Alimentos': [6.0, 21.0, 10.0, 63.0, 0, 0],
};

const dataIndustries2024 = {
  'Alimentos': [6.0, 8.0, 12.0, 14.0, 24.0, 36.0],
};

const options = {
  indexAxis: 'y',
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'bottom',
      labels: {
        font: { size: 14, weight: 'bold' },
      },
    },
    tooltip: {
      callbacks: {
        label: (context) => `${context.dataset.label}: ${context.raw}%`,
      },
    },
    datalabels: {
      color: '#000',
      font: { weight: 'bold', size: 10 },
      anchor: 'end',
      align: 'end',
      formatter: (value) => `${Math.round(value)}%`,
    },
  },
  scales: {
    x: {
      beginAtZero: true,
      max: 100,
      title: {
        display: true,
        text: 'Porcentaje (%)',
        font: { size: 12 },
      },
      ticks: { stepSize: 10, font: { size: 10 } },
    },
    y: {
      title: {
        display: true,
        text: 'Categoría de Facturación',
        font: { size: 12 },
      },
      ticks: {
        font: { size: 10 },
      },
    },
  },
};

const DistribucionFacturacionLine = ({ selectedCategories = allLabels, selectedIndustries }) => {
  const [infoOpen, setInfoOpen] = useState(false);

  const industriesToUse = selectedIndustries && selectedIndustries.length > 0
    ? selectedIndustries
    : Object.keys(dataIndustries2023);

  const filteredLabels = selectedCategories.length > 0
    ? allLabels.filter((label) => selectedCategories.includes(label))
    : allLabels;

  const selectedData2023 = industriesToUse
    .filter((industry) => dataIndustries2023[industry])
    .map((industry) => dataIndustries2023[industry]);

  const selectedData2024 = industriesToUse
    .filter((industry) => dataIndustries2024[industry])
    .map((industry) => dataIndustries2024[industry]);

  const calculateAverageData = (dataArray) => {
    return filteredLabels.map((_, index) => {
      const values = dataArray.map((data) => data[allLabels.indexOf(filteredLabels[index])]);
      const numericValues = values.filter((value) => typeof value === 'number');
      const sum = numericValues.reduce((acc, curr) => acc + curr, 0);
      const avg = numericValues.length > 0 ? sum / numericValues.length : 0;
      return parseFloat(avg.toFixed(2));
    });
  };

  const averageData2023 = calculateAverageData(selectedData2023);
  const averageData2024 = calculateAverageData(selectedData2024);

  const chartData = {
    labels: filteredLabels,
    datasets: [
      {
        label: '2023',
        data: averageData2024,
        backgroundColor: '#1F3D58', // verde oscuro
      },
      {
        label: '2022',
        data: averageData2023,
        backgroundColor: '#717171', // beige claro
      },
    ],
  };

  const infoContent = {
    Analisis: 'En 2023, la mayoría de las empresas tienen una facturación superior a 250 MDP, consolidándose como el grupo más fuerte. Las empresas con ingresos intermedios tienen menor presencia, mientras que las de menor facturación son las menos frecuentes, reflejando un mercado dominado por compañías de gran escala.',
    formula: 'Promedio de Facturación = Total Facturación por Categoría / Total de Industrias',
    description: 'La gráfica muestra el promedio de facturación anual para diferentes categorías de facturación en las industrias seleccionadas.',
  };

  return (
    <div style={{ width: '100%', height: '555px' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <MuiTooltip title="Información de la gráfica">
          <IconButton onClick={() => setInfoOpen(true)}>
            <InfoIcon />
          </IconButton>
        </MuiTooltip>
      </Box>
      <Bar data={chartData} options={options} />
      <Dialog open={infoOpen} onClose={() => setInfoOpen(false)}>
        <DialogTitle>Información de la Gráfica</DialogTitle>
        <DialogContent>
          <p><strong>Análisis:</strong> {infoContent.Analisis}</p>
          <p><strong>Fórmula:</strong> {infoContent.formula}</p>
          <p><strong>Descripción:</strong> {infoContent.description}</p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoOpen(false)} color="primary">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default DistribucionFacturacionLine;
